import { cn } from '@v2/shared/lib/utils';
import Stars from './Stars';
import { ReviewSource, ReviewSummary } from './types';

type Props = {
  summary: ReviewSummary;
  source: ReviewSource;
};

/**
 * The score, the distribution and the platform that collected them.
 *
 * The count and the bars come from the reviews endpoint, which answers for exactly the
 * set we ask it for, so the bars always add up to the number printed beside them. The
 * average is SmartCustomer's own headline — the two together are the 4.5 from 469 that
 * their widget shows and that a shopper finds on their profile, which is the point: a
 * figure someone can go and check has to match when they check it.
 *
 * Rendered by the server, and never in a loading state. It is the first thing on the
 * page below the hero, and a shopper deciding whether to trust us should not watch it
 * arrive.
 */
export default function RatingSummary({ summary, source }: Props) {
  if (summary.reviewCount < 1) {
    return null;
  }

  return (
    <section
      aria-labelledby="rating-summary"
      className={cn(
        'relative z-10 -mt-16 mx-4 rounded-xl border border-solid border-slate-200 bg-white p-6 shadow-lg',
        'md:mx-7 md:p-8',
        'grid gap-8 lg:grid-cols-[auto_1fr_auto] lg:items-center'
      )}
    >
      <h2 id="rating-summary" className="sr-only">
        Rating summary
      </h2>

      <div className="text-center lg:border-0 lg:border-r lg:border-solid lg:border-slate-200 lg:pr-8">
        <div className="text-5xl font-extrabold leading-none tracking-tight text-slate-900">
          {summary.averageRating.toFixed(1)}
        </div>

        <Stars rating={summary.averageRating} className="my-1 text-xl" />

        <div className="text-sm text-slate-500">
          <b className="text-slate-900">{summary.label}</b> · {summary.reviewCount.toLocaleString('en-US')}{' '}
          verified reviews
        </div>
      </div>

      <ul className="grid list-none gap-1.5 m-0 p-0 lg:min-w-[260px]">
        {summary.distribution.map((row) => (
          <li key={row.stars} className="grid grid-cols-[34px_1fr_48px] items-center gap-2.5 text-xs text-slate-600">
            <span>{row.stars} ★</span>
            <span className="h-2 overflow-hidden rounded-full bg-slate-200">
              <i className="block h-full rounded-full bg-primary" style={{ width: `${row.percent}%` }} />
            </span>
            <span className="text-right">{row.count.toLocaleString('en-US')}</span>
          </li>
        ))}
      </ul>

      <a
        href={source.url}
        target="_blank"
        rel="noopener nofollow"
        className={cn(
          'flex items-center gap-2 self-start rounded-lg border border-solid border-slate-200 bg-slate-50',
          'px-3 py-2 text-xs font-semibold text-slate-700 no-underline hover:border-slate-300'
        )}
      >
        <span className="h-2 w-2 rounded-full bg-emerald-600" aria-hidden />
        Verified by {source.name}
      </a>
    </section>
  );
}
